import { ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  DragTypeEnum,
  IRideDto,
  IRydeRow,
  isRowExpandedSF,
  isStarredTableRowIdSF,
  ridesDraggingDataAtom,
  shouldShowUserUpdaterIndicator,
  useIsMobile,
} from "@ryde/shared";
import { purpleMui, rydeColors } from "@ryde/theme";

import { FramerTooltip } from "../../../common";
import { CheckboxStar, GridDetailsToggle } from "../../../controls";
import { MultiIconCellRidesStyled } from "../../generic";
import { CellMultiIconRidesNearBy } from "./cell.multi-icon.rides.near-by";
import { HubIndicatorRides } from "./hub-indicator";
import { CellMultiIconDragOrSelectHandle } from "./parts";
import { CourseIndicatorRides, UserUpdaterIndicatorRides } from "./summary";

interface ICellMultiIconRides {
  row: IRydeRow<IRideDto>;
}

export const CellMultiIconRides = ({
  row,
}: ICellMultiIconRides): ReactElement => {
  const rideDto = row.original;
  const rideId = row.original.id;
  const canExpand = row.getCanExpand();
  const { isMobile } = useIsMobile();
  const { t } = useTranslation();
  const { entity } = usePageContext();

  const [isStarred, setIsStarred] = useRecoilState(
    isStarredTableRowIdSF([entity, rideId]),
  );
  const handleStarredToggle = useEvent((): void => {
    setIsStarred(starred => !starred);
  });

  const { type: dragType } = useRecoilValue(ridesDraggingDataAtom);

  const isTooltipDisabled = useMemo(() => {
    return dragType && dragType !== DragTypeEnum.NOT_DRAGGING;
  }, [dragType]);

  const [isRowExpanded, setIsRowExpanded] = useRecoilState(
    isRowExpandedSF([entity, rideId]),
  );

  const toggleExpandDetailPanel = useEvent(() => {
    setIsRowExpanded(expanded => !expanded);
  });

  return (
    <MultiIconCellRidesStyled>
      <CellMultiIconDragOrSelectHandle rideDto={rideDto} row={row} />

      {!isMobile && (
        <FramerTooltip
          tooltipDisabled={isTooltipDisabled}
          content={t("ride.RIDE_STAR_TOOLTIP")}
        >
          <CheckboxStar
            checked={isStarred}
            strokeColor={rideDto?.courseId ? purpleMui._300 : rydeColors.grey}
            onCheckboxToggle={handleStarredToggle}
          />
        </FramerTooltip>
      )}
      <CellMultiIconRidesNearBy rideDto={rideDto} />

      {!isMobile && canExpand && (
        <FramerTooltip
          tooltipDisabled={isTooltipDisabled}
          content={t("ride.RIDE_QUICK_VIEW_TOOLTIP")}
        >
          <GridDetailsToggle
            onToggleState={toggleExpandDetailPanel}
            expanded={isRowExpanded}
          />
        </FramerTooltip>
      )}

      {!isMobile && !!rideDto?.courseId && (
        <span className="course_icon_wrapper">
          <CourseIndicatorRides rideDto={rideDto} />
        </span>
      )}

      {!isMobile && <HubIndicatorRides rideDto={rideDto} />}

      {!isMobile && shouldShowUserUpdaterIndicator(rideDto) && (
        <UserUpdaterIndicatorRides rideDto={rideDto} />
      )}
    </MultiIconCellRidesStyled>
  );
};
